import { type SelectProps } from "@mantine/core";
import { useCallback } from "react";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import { Select as MantineSelect } from "src/components/shared/MantineCombobox";
import { normalizeSelectValue } from "src/utils/mantineComboboxData";

interface Props<T extends FieldValues> extends SelectProps {
  name: Path<T>;
  control: Control<T>;
  onChange?: (value: string) => void | Promise<void>;
  required?: boolean;
}

function Select<T extends FieldValues>({
  name,
  control,
  onChange,
  required,
  ...props
}: Props<T>) {
  const {
    field: { onChange: defaultOnChange, ...field },
    fieldState: { invalid },
  } = useController({
    name,
    control,
    rules: { required },
  });

  const handleChange = useCallback(
    async (value: string | null) => {
      defaultOnChange(value ?? "");
      if (onChange && value) await onChange(value);
    },
    [defaultOnChange, onChange],
  );

  const selectValue = normalizeSelectValue(field.value);

  return (
    <MantineSelect
      {...field}
      {...props}
      value={selectValue}
      onChange={handleChange}
      error={invalid}
    />
  );
}

export default Select;
